import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { ApiError } from 'src/app/core/models/api-error';
import { AppLoaderService } from 'src/app/core/services/app-loader/app-loader.service';
import { AppSessionService } from 'src/app/core/services/app-session/app-session.service';
import { BannerService } from 'src/app/core/services/banner/banner.service';
import { isValidControl } from 'src/app/core/utilities/forms';
import { isValidSize, toBase64 } from 'src/app/core/utilities/files';
import { NotEmptyValidator } from 'src/app/core/validators/not-empty-validator';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { Setting } from 'src/app/core/models/setting';
import { SettingsService } from 'src/app/core/services/settings/settings.service';

@Component({
  selector: 'shared-settings-banner',
  templateUrl: './settings.component.html'
})
export class SettingsComponent {

  @Output('onCloseBanner')
  public onCloseBanner: EventEmitter<void> = new EventEmitter();

  private _setting?: Setting;

  public form: FormGroup = this._formBuilder.group({
    base64: [''],
    fileExtension: ['', [Validators.maxLength(10)]],
    fileName: [''],
    noticeConfidentiality: ['', Validators.required],
    resetNoticeConfidentiality: [false],
  });

  public isLoadingBanner: boolean = false;
  public loaderMessage: string = 'Cargando...';

  public isClosingModal: boolean = false;
  public isOpenModal: boolean = false;

  constructor(
    private readonly _appLoaderService: AppLoaderService,
    private readonly _appSessionService: AppSessionService,
    private readonly _bannerService: BannerService,
    private readonly _formBuilder: FormBuilder,
    private readonly _notificationService: NotificationService,
    private readonly _router: Router,
    private readonly _settingsService: SettingsService,
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  public get isClosingBanner(): boolean {
    return this._bannerService.isClosing;
  }

  /**
   * Set initial state to component.
   */
  public initializeComponent(): void {
    this.isLoadingBanner = true;

    this._settingsService
      .getSettings$()
      .subscribe({
        next: (response) => {
          this._setting = response;

          if (this._setting) {
            this.form = this._formBuilder.group({
              noticeConfidentiality: [this._setting.noticeConfidentiality, [Validators.required, Validators.maxLength(500), NotEmptyValidator]],
              resetNoticeConfidentiality: [false],
              fileExtension: ['', [Validators.maxLength(10)]],
              base64: [''],
              fileName: ['']
            });
          }

          this.isLoadingBanner = false;
        },
        error: (error: ApiError) => {
          this.onCloseBannerHandler();
          this.isLoadingBanner = false;
          this._notificationService.warning('Ocurrio un error', `No fue posible obtener la información de la configuración del sistema.\n${error.details}`);
        }
      });
  }

  /**
   * Close banner function.
   */
  public onCloseBannerHandler = (): void => this._bannerService.closeBanner();

  /**
   * Validate that a form control is valid.
   * @param name Form control name.
   * @returns Flag to validate if a form control is valid.
   */
  public isValidControl = (name: string): boolean => isValidControl(name, this.form);

  /**
  * Event handler when file is selected.
  * @param event Event.
  */
  public async onChangeFile(event: any): Promise<void> {
    await this.setFile(event.target.files[0]);
  }

  /**
   * Set value to form when file is selected and set value to input reference.
   * @param file file to set value to form. 
   * @param setValueToInput set new value to input.
  */
  public async setFile(file: File | undefined = undefined): Promise<void> {
    this.form.controls['fileName'].removeValidators(Validators.required);

    this.form.patchValue({ fileName: '', base64: '', fileExtension: '' });

    if (!file) return;

    if (`.${file.name.split('.').pop() ?? ''}` !== '.pdf' || !isValidSize(file.size)) {
      this.form.controls['fileName'].addValidators(Validators.required);

      this.form.patchValue({ fileName: undefined });

      this.form.controls['fileName'].markAsTouched();
      return;
    }

    this.form.patchValue({
      base64: (await toBase64(file)).split(',')[1],
      fileExtension: `.${file.name.split('.').pop() ?? ''}`,
      fileName: file.name,
    });
  }

  /**
   * Set new setting information.
   */
  public save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid)
      return;

    const setting = this.form.value as Setting;

    this.isLoadingBanner = true;
    this.loaderMessage = "Guardando configuración..."

    this._settingsService
      .setSettings$(setting)
      .subscribe({
        next: () => {
          this._notificationService.success('Registro exitoso', 'Se actualizo correctamente la información de la configuración del sistema');
          if (setting.resetNoticeConfidentiality) {
            this._appLoaderService.startLoading();
            setTimeout(() => {
              this.onCloseBannerHandler();
              this._appSessionService.removeSession();
              this._settingsService.loadedSettings = false;
              this._router.navigateByUrl('/');
              window.location.reload();
            }, 1000);
          } else {
            this.isLoadingBanner = false;
            this.onCloseBannerHandler();
          }
        },
        error: (error: ApiError) => {
          this.isLoadingBanner = false;
          this._notificationService.warning('Ocurrio un error', `No fue posible actualizar la configuración del sistema.\n${error.details}`);
        }
      })
  }
}